import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutGrid, LineChart, History, Plug, Settings as SettingsIcon,
  LogOut, Activity, Menu, X,
} from "lucide-react";
import TopStepOnboardingPopup from "@/components/TopStepOnboardingPopup";

const navItems = [
  { to: "/app", label: "Dashboard", icon: LayoutGrid, testid: "nav-dashboard" },
  { to: "/signals", label: "Signals", icon: Activity, testid: "nav-signals" },
  { to: "/trades", label: "Trades", icon: History, testid: "nav-trades" },
  { to: "/brokers", label: "Brokers", icon: Plug, testid: "nav-brokers" },
  { to: "/settings", label: "Strategy", icon: SettingsIcon, testid: "nav-settings" },
];

function Brand() {
  return (
    <div className="px-5 py-5 border-b border-white/10">
      <div className="flex items-center gap-2">
        <LineChart className="w-5 h-5 text-teal-400" strokeWidth={1.5} />
        <div className="font-mono text-sm tracking-widest uppercase">SCALP.OS</div>
      </div>
      <div className="text-[10px] text-zinc-500 mt-1 tracking-widest uppercase">v0.1 // 5m engine</div>
    </div>
  );
}

function NavList({ onItemClick }) {
  return (
    <nav className="flex-1 py-3 px-2 space-y-0.5">
      {navItems.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.to === "/app"}
          data-testid={it.testid}
          onClick={onItemClick}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors ${
              isActive
                ? "bg-white/[0.06] text-zinc-50"
                : "text-zinc-400 hover:text-zinc-100 hover:bg-white/[0.03]"
            }`
          }
        >
          <it.icon className="w-4 h-4" strokeWidth={1.5} />
          <span>{it.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function SessionFooter({ email, onLogout }) {
  return (
    <div className="border-t border-white/10 p-3">
      <div className="px-2 pb-2">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Session</div>
        <div className="text-sm text-zinc-200 truncate" data-testid="user-email">{email}</div>
      </div>
      <button
        data-testid="logout-button"
        onClick={onLogout}
        className="w-full flex items-center gap-2 px-2 py-2 text-sm text-zinc-400 hover:text-rose-400 hover:bg-white/[0.03] rounded-sm transition-colors"
      >
        <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out
      </button>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleLogout = async () => { await logout(); navigate("/login"); };
  const closeDrawer = () => setDrawerOpen(false);

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100 flex">
      {/* Desktop sidebar (lg+) */}
      <aside
        className="hidden lg:flex w-56 shrink-0 border-r border-white/10 flex-col"
        data-testid="app-sidebar"
      >
        <Brand />
        <NavList />
        <SessionFooter email={user?.email} onLogout={handleLogout} />
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={closeDrawer}
            data-testid="mobile-drawer-backdrop"
          />
          <aside className="relative w-64 max-w-[80vw] bg-[#09090B] border-r border-white/10 flex flex-col h-full">
            <Brand />
            <NavList onItemClick={closeDrawer} />
            <SessionFooter email={user?.email} onLogout={handleLogout} />
          </aside>
          <button
            onClick={closeDrawer}
            className="absolute top-3 right-3 text-zinc-300 hover:text-zinc-50 p-2 rounded-sm bg-white/5 z-10"
            aria-label="Close menu"
            data-testid="mobile-drawer-close"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <header className="lg:hidden sticky top-0 z-40 bg-[#09090B]/95 backdrop-blur border-b border-white/10 px-4 py-3 flex items-center justify-between">
          <button
            data-testid="mobile-menu-button"
            onClick={() => setDrawerOpen(true)}
            className="text-zinc-300 hover:text-zinc-50 p-1 -ml-1"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <div className="flex items-center gap-2">
            <LineChart className="w-4 h-4 text-teal-400" strokeWidth={1.5} />
            <div className="font-mono text-xs tracking-widest uppercase">SCALP.OS</div>
          </div>
          <button
            onClick={handleLogout}
            className="text-zinc-500 hover:text-rose-400 p-1 -mr-1"
            aria-label="Sign out"
            data-testid="mobile-logout-button"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </header>

        <main className="flex-1 overflow-x-hidden">
          {children}
        </main>
        <TopStepOnboardingPopup />
      </div>
    </div>
  );
}
